import { apiAuth } from "@/api/auth/auth.api";
import { CodeInput } from "@/components/code-input/CodeInput.tsx";
import { sendTokenToExtension } from "@/helpers/send-to-extension.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { FullScreenCard } from "@/pages/security/components/FullScreenCard.tsx";
import { TwoFactorVerifyForm } from "@/pages/security/components/TwoFactorVerifyForm.tsx";
import { getAuthorizationFlow } from "@/pages/security/authorization.helper.ts";
import { getApiError, getInviteFlow, hasValidationError } from "@/pages/security/invite.helpers.ts";
import {
  useEmployeeChangePassword,
  useEmployeeMeInvalidate,
  useEmployeeResetPassword,
  useInviteCodeRequest,
  useInviteCodeVerify,
  useInviteLoginMethods,
} from "@/queries/employees/use-employees.query.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { useAuthStore } from "@/stores/employee.store.ts";
import { ApiAuthorization, ChangePasswordSource } from "@/types/employees/employee.types.ts";
import { AppDistribution } from "@/types/common.types.ts";
import { InviteFlow, InviteLoginMethod } from "@/types/security.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  FontAwesomeIcon,
  Input,
  LinkTo,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useEffect, useEffectEvent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { z } from "zod";
import { isAxiosError } from "axios";

const LOGIN_METHOD_BUTTONS: Record<InviteLoginMethod, { icon: string; labelKey: string }> = {
  [InviteLoginMethod.google]: {
    icon: "fa-brands fa-google",
    labelKey: "common:log_in_with_google",
  },
  [InviteLoginMethod.entra]: {
    icon: "fa-brands fa-microsoft",
    labelKey: "common:log_in_with_microsoft",
  },
  [InviteLoginMethod.oidc]: { icon: "fa-light fa-key", labelKey: "common:log_in_with_sso" },
};

export default function Invite({ source }: { source: ChangePasswordSource }) {
  const { t } = useTranslation();
  const { token } = useParams();
  const { handleErrors } = useErrorHandle();
  useTitle(t("core:welcome_to_platform"));
  const { setToken } = useAuthStore();

  const InviteSchemaObject = z.object({
    password: z
      .string({ error: t("errors:password_required") })
      .min(1, t("errors:password_required"))
      .min(8, t("api_errors:minimum_value_must", { minimum: 8 }))
      .regex(/[a-z]/, t("api_errors:password_must_contain_lowercase"))
      .regex(/[A-Z]/, t("api_errors:password_must_contain_uppercase"))
      .regex(/[0-9]/, t("api_errors:password_must_contain_number"))
      .regex(/[!@#$%^&*(),.?":{}|<>]/, t("api_errors:password_must_contain_special")),
  });

  type InviteSchema = z.infer<typeof InviteSchemaObject>;

  const InviteCodeSchemaObject = z.object({
    code: z
      .string({ error: t("errors:field_required") })
      .regex(/^\d{6}$/, t("api_errors:invite_code_invalid")),
  });

  type InviteCodeSchema = z.infer<typeof InviteCodeSchemaObject>;

  const navigate = useNavigate();

  const passwordForm = useForm<InviteSchema>({
    mode: "onSubmit",
    resolver: zodResolver(InviteSchemaObject),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = passwordForm;

  const codeForm = useForm<InviteCodeSchema>({
    mode: "onSubmit",
    resolver: zodResolver(InviteCodeSchemaObject),
    defaultValues: { code: "" },
  });

  const onSubmit = handleSubmit((data) => {
    resetPasswordAction(data);
  });

  const flow = getInviteFlow(token);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [pendingPassword, setPendingPassword] = useState<string>();
  const [tfaAccessToken, setTfaAccessToken] = useState<string>();
  const [tfaStep, setTfaStep] = useState<"before_password" | "after_password">();
  const [loadingLoginMethod, setLoadingLoginMethod] = useState<InviteLoginMethod>();
  const [isInvalidLink, setInvalidLink] = useState<boolean>(false);
  const {
    data: loginMethods,
    isFetching: isLoginMethodsFetching,
    isSuccess: isLoginMethodsSuccess,
    isError: isLoginMethodsError,
    error: loginMethodsError,
    refetch: refetchLoginMethods,
  } = useInviteLoginMethods(token, flow === InviteFlow.password);
  const [hasPasswordLoginDisabledError, setPasswordLoginDisabledError] = useState(false);
  const isPasswordLoginDisabled =
    flow === InviteFlow.password &&
    (hasPasswordLoginDisabledError ||
      (loginMethods !== undefined && !loginMethods.login_methods.includes("password")));
  const availableLoginMethods = (loginMethods?.login_methods ?? []).filter(
    (method): method is InviteLoginMethod =>
      method !== "password" &&
      (method !== InviteLoginMethod.entra ||
        import.meta.env.VITE_APP_DISTRIBUTION !== AppDistribution.headly)
  );

  const loginMethodsApiError = getApiError(loginMethodsError);
  const isInvalidLoginMethodsToken =
    (isAxiosError(loginMethodsApiError) && loginMethodsApiError.response?.status === 403) ||
    ["reset_password_token_invalid", "reset_password_token_expired", "email_not_registered"].some(
      (code) => hasValidationError(loginMethodsApiError, code)
    );

  const employeeMeInvalidate = useEmployeeMeInvalidate();
  const changePasswordMutation = useEmployeeChangePassword(control);
  const resetPasswordMutation = useEmployeeResetPassword();
  const {
    mutate: requestInitialInviteCode,
    mutateAsync: requestInviteCode,
    isPending: isInviteCodeRequestPending,
  } = useInviteCodeRequest();
  const { mutateAsync: verifyCode, isPending: isInviteCodeVerifyPending } = useInviteCodeVerify();

  const handleInitialCodeRequest = useEffectEvent((error?: unknown) => {
    const apiError = getApiError(error);
    if (!error) {
      codeForm.reset({ code: "" });
    } else if (hasValidationError(apiError, "invite_link_invalid")) {
      setInvalidLink(true);
    } else {
      handleErrors(apiError, codeForm.control);
    }
  });

  useEffect(() => {
    if (!token) return;

    setToken(null);

    if (flow === InviteFlow.code) {
      requestInitialInviteCode(token, {
        onSuccess: () => handleInitialCodeRequest(),
        onError: handleInitialCodeRequest,
      });
    }
  }, [token, flow, setToken, requestInitialInviteCode]);

  const completeInvite = (accessToken: string) => {
    setToken(accessToken);
    sendTokenToExtension(accessToken);
    employeeMeInvalidate();
    analyticsService.trackEvent(analyticEvents.inviteAccepted);
    setIsRedirecting(true);
    navigate("/");
  };

  async function handleOAuthLogin(method: InviteLoginMethod) {
    if (method === InviteLoginMethod.oidc) {
      navigate("/login");
      return;
    }

    setLoadingLoginMethod(method);
    const { data, error } = await (method === InviteLoginMethod.google
      ? apiAuth.getGoogleLoginUrl()
      : apiAuth.getEntraLoginUrl());

    if (data) {
      window.location.assign(data.authorization_url);
    } else {
      setLoadingLoginMethod(undefined);
      handleErrors(error);
    }
  }

  async function resetPasswordAction(loginData: InviteSchema) {
    if (!token) return;

    setToken(null);

    setLoading(true);
    try {
      const data = await resetPasswordMutation.mutateAsync(token);
      await handleAuthorization(data, loginData.password);
    } catch (error) {
      const apiError = getApiError(error);
      if (isAxiosError(apiError) && apiError.response?.status === 403) {
        setInvalidLink(true);
      } else if (hasValidationError(apiError, "login_method_password_disabled")) {
        setPasswordLoginDisabledError(true);
        await refetchLoginMethods();
      } else {
        handleErrors(apiError, control);
      }
    }
    setLoading(false);
  }

  async function changePassword(password: string, accessToken: string) {
    try {
      const data = await changePasswordMutation.mutateAsync({ password, source, accessToken });

      const authorizationFlow = getAuthorizationFlow(data);
      if (authorizationFlow === "tfa_code") {
        setTfaStep("after_password");
        setTfaAccessToken(data.access_token);
        return true;
      }

      if (authorizationFlow === "tfa_setup") {
        setIsRedirecting(true);
        navigate(`/tfa-setup/${data.access_token}`);
        return true;
      }

      completeInvite(data.access_token);
      return true;
    } catch {
      return false;
    }
  }

  async function handleAuthorization(authorization: ApiAuthorization, password: string) {
    const authorizationFlow = getAuthorizationFlow(authorization);

    if (authorizationFlow === "tfa_code") {
      setPendingPassword(password);
      setTfaStep("before_password");
      setTfaAccessToken(authorization.access_token);
      return;
    }

    if (authorizationFlow === "tfa_setup") {
      navigate(`/tfa-setup/${authorization.access_token}`);
      return;
    }

    await changePassword(password, authorization.access_token);
  }

  async function handleTfaVerifySuccess(accessToken: string) {
    if (tfaStep === "after_password") {
      completeInvite(accessToken);
      return;
    }

    if (!pendingPassword) return;

    const isPasswordChanged = await changePassword(pendingPassword, accessToken);
    if (!isPasswordChanged) setTfaAccessToken(undefined);
  }

  const requestNewCode = async () => {
    if (!token) return;

    try {
      await requestInviteCode(token);
      codeForm.reset({ code: "" });
      return;
    } catch (error) {
      const apiError = getApiError(error);
      if (hasValidationError(apiError, "invite_link_invalid")) {
        setInvalidLink(true);
      } else {
        handleErrors(apiError, codeForm.control);
      }
    }
  };

  const verifyInviteCode = codeForm.handleSubmit(async ({ code }) => {
    if (!token) return;

    try {
      const data = await verifyCode({ token, code });
      completeInvite(data.access_token);
      return;
    } catch (error) {
      const apiError = getApiError(error);

      if (hasValidationError(apiError, "invite_link_invalid")) {
        setInvalidLink(true);
        return;
      }

      handleErrors(apiError, codeForm.control);
    }
  });

  return (
    <FullScreenCard>
      <Stack gap={"sm"} className={"w-full sm:max-w-md "}>
        {isRedirecting ? (
          <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
            <Button size="lg" className="w-full" isLoading disabled>
              {t("common:loading")}
            </Button>
          </Box>
        ) : tfaAccessToken ? (
          <TwoFactorVerifyForm
            key={tfaAccessToken}
            accessToken={tfaAccessToken}
            onSuccess={handleTfaVerifySuccess}
          />
        ) : (
          <Box className="w-full p-6 lg:p-8 bg-light rounded-xs shadow-md">
            <Stack gap={"2xl"}>
              <a
                href={import.meta.env.VITE_LANDING_URL + "/"}
                className="flex items-center mb-6 text-2xl font-semibold gap-x-[6px]"
              >
                <img src="/logo.png" alt="Skailer" className="h-[28px] w-auto" />
                <Text className={"text-[22px] leading-[24px]"}>{t("core:app_name")}</Text>
              </a>
              {isInvalidLink || isInvalidLoginMethodsToken ? (
                <Stack gap={"lg"}>
                  <Title header={t("core:invite_invalid_title")} size={"lg"} />
                  <Text className={"text-danger"}>{t("core:invite_invalid_description")}</Text>
                  <Stack gap={"sm"}>
                    <Button size="lg" className="w-full" onClick={() => navigate("/login")}>
                      {t("common:login")}
                    </Button>
                    <Button
                      size="lg"
                      variant="secondary"
                      className="w-full"
                      onClick={() => navigate("/forgot-password")}
                    >
                      {t("core:restore_password")}
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack gap={"lg"}>
                  <Title
                    header={t("core:welcome_to_platform")}
                    size={"lg"}
                    caption={`${t("core:join_workspace_of_your_team")}`}
                  />
                  {flow === InviteFlow.loading ||
                  (flow === InviteFlow.password &&
                    (isLoginMethodsFetching ||
                      (!isLoginMethodsSuccess && !isLoginMethodsError))) ? (
                    <Button size="lg" className="w-full" isLoading disabled>
                      {t("common:loading")}
                    </Button>
                  ) : flow === InviteFlow.password && isLoginMethodsError ? (
                    <Stack gap="sm">
                      <Text className="text-danger">{t("errors:server_error")}</Text>
                      <Button onClick={() => void refetchLoginMethods()}>
                        {t("common:retry")}
                      </Button>
                    </Stack>
                  ) : isPasswordLoginDisabled ? (
                    <Stack gap={"xs"}>
                      {availableLoginMethods.map((method) => (
                        <Button
                          key={method}
                          size="lg"
                          className="w-full"
                          onClick={() => handleOAuthLogin(method)}
                          isLoading={loadingLoginMethod === method}
                          disabled={loadingLoginMethod !== undefined}
                          leftIcon={<FontAwesomeIcon icon={LOGIN_METHOD_BUTTONS[method].icon} />}
                        >
                          {t(LOGIN_METHOD_BUTTONS[method].labelKey)}
                        </Button>
                      ))}
                    </Stack>
                  ) : flow === InviteFlow.code ? (
                    <>
                      <Text>{t("core:invite_code_description")}</Text>
                      <form onSubmit={verifyInviteCode}>
                        <Controller
                          control={codeForm.control}
                          name="code"
                          render={({ field }) => (
                            <CodeInput
                              label={t("core:two_factor_enter_code")}
                              value={field.value}
                              onChange={field.onChange}
                              error={codeForm.formState.errors.code?.message}
                              disabled={isInviteCodeVerifyPending || isInviteCodeRequestPending}
                              autoFocus
                            />
                          )}
                        />
                      </form>
                      <Stack gap={"md"}>
                        <ServerErrorField errors={codeForm.formState.errors} />
                        <Button
                          type="submit"
                          size="lg"
                          onClick={verifyInviteCode}
                          className="w-full"
                          isLoading={isInviteCodeVerifyPending}
                          disabled={isInviteCodeVerifyPending || isInviteCodeRequestPending}
                        >
                          {t("common:continue")}
                        </Button>
                        <Button
                          variant="plain"
                          onClick={requestNewCode}
                          className="w-full"
                          isLoading={isInviteCodeRequestPending}
                          disabled={isInviteCodeVerifyPending || isInviteCodeRequestPending}
                        >
                          {t("core:invite_code_resend")}
                        </Button>
                      </Stack>
                    </>
                  ) : (
                    <>
                      <form className="" onSubmit={onSubmit}>
                        <Input
                          label={t("common:set_your_password")}
                          type="password"
                          {...register("password")}
                          placeholder="••••••••"
                          error={errors.password?.message}
                        />
                      </form>
                      <ServerErrorField errors={errors} />
                      <Button
                        type={"submit"}
                        size="lg"
                        onClick={onSubmit}
                        className="w-full"
                        isLoading={isLoading}
                        disabled={isLoading}
                      >
                        {t("common:accept_invitation")}
                      </Button>
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        )}
        <Box className={"text-secondary"}>
          <Text>{t("core:sign_up_accept_invitation_agree")}</Text>{" "}
          <LinkTo to={import.meta.env.VITE_LANDING_URL + "/terms"} target={"_blank"}>
            {t("common:agree_with_terms_of_service")}
          </LinkTo>{" "}
          <Text>{t("common:and")}</Text>{" "}
          <LinkTo to={import.meta.env.VITE_LANDING_URL + "/privacy"} target={"_blank"}>
            {t("common:agree_with_privacy_policy")}
          </LinkTo>
        </Box>
      </Stack>
    </FullScreenCard>
  );
}
